import { logger } from '@/utils/logger';
import { Icon } from '@/styles/Icon';
import { ErrorText } from '@/styles/Typography';
import React, { useEffect, useRef, useState } from 'react';
import { styled } from 'styled-components';
import { GradientButton } from '../button/Button';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { LoadingSpinnerSmall } from '../common/LoadingSpinner';
import { useSnackbarStore } from '@/stores/snackbarStore';
import { Modal } from '../modal/Modal';
import { ToggleIconButton } from '@/styles/IconButton';
import {
  FormField,
  FormLabel,
  FormInput,
  FormSuccess,
  PasswordInputContainer as SharedPasswordInputContainer,
  InputActionButton,
} from '@/styles/Form';

// Input 타입 정의
type InputType = 'email' | 'password' | 'text' | 'date' | 'file';

interface BaseInputProps {
  type?: InputType;
  name: string;
  placeholder?: string;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  error?: string;
  disabled?: boolean;
  required?: boolean;
  autoFocus?: boolean;
  label?: string;
  pattern?: string;
  title?: string;
  autoCorrect?: string;
  maxLength?: number;
}

interface EmailInputProps extends BaseInputProps {
  onSend?: () => Promise<boolean>;
  sendButtonText?: string;
  isLoading?: boolean;
  isSent?: boolean;
  successMessage?: string;
}

interface EmailVerificationInputProps extends BaseInputProps {
  onVerify?: () => void;
  verifyButtonText?: string;
  isLoading?: boolean;
  isVerified?: boolean;
  successMessage?: string;
}

interface PasswordInputProps extends BaseInputProps {
  showPassword?: boolean;
  onTogglePassword?: () => void;
  helperText?: string;
  autoComplete?: string;
}

interface PasswordConfirmProps extends BaseInputProps {
  originalPassword: string;
}

interface DescriptionInputProps extends Omit<BaseInputProps, 'type' | 'onChange' | 'onBlur'> {
  maxLength?: number;
  height?: number;
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
}

// 기본 Input 컴포넌트
export const BaseInput: React.FC<BaseInputProps> = ({
  type = 'text',
  name,
  placeholder,
  value,
  maxLength,
  onChange,
  onBlur,
  onKeyDown,
  error,
  disabled,
  required,
  autoFocus,
  label,
}) => {
  return (
    <InputWrapper>
      {label && (
        <Label htmlFor={name}>
          {label}
          {required && <RequiredAsterisk>*</RequiredAsterisk>}
        </Label>
      )}
      <StyledInput
        id={name}
        type={type}
        name={name}
        placeholder={placeholder}
        value={value}
        maxLength={maxLength}
        autoComplete="off"
        onChange={onChange}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        disabled={disabled}
        required={required}
        autoFocus={autoFocus}
        state={error ? 'error' : 'default'}
      />
      {error && <ErrorText>{error}</ErrorText>}
    </InputWrapper>
  );
};

// 이메일 Input 컴포넌트
export const EmailInput: React.FC<EmailInputProps> = ({
  onSend,
  sendButtonText = 'Send',
  isLoading = false,
  isSent: isSentProp = false,
  successMessage,
  ...props
}) => {
  const [showResendModal, setShowResendModal] = useState(false);
  const [internalIsSent, setInternalIsSent] = useState(isSentProp);
  const { showSnackbar } = useSnackbarStore();

  useEffect(() => {
    setInternalIsSent(isSentProp);
  }, [isSentProp]);

  const getButtonText = () => {
    if (isLoading) return <LoadingSpinnerSmall />;
    if (internalIsSent) return 'Resend';
    return sendButtonText || 'Send';
  };

  const handleSendClick = () => {
    if (internalIsSent) {
      // Resend 상태일 때는 모달 표시
      setShowResendModal(true);
    } else {
      // 첫 번째 Send일 때
      handleSend();
    }
  };

  const handleSend = async () => {
    try {
      if (onSend) {
        const success = await onSend();

        if (success) {
          showSnackbar('A verification code has been sent to your email.', {
            showCheckIcon: true,
            duration: 3000,
          });

          setInternalIsSent(true);
        }
      }
    } catch (error) {
      logger.error('EmailInput: onSend 실행 중 에러:', { context: { error } });
      showSnackbar(error instanceof Error ? error.message : 'Failed to send verification code.', {
        showCheckIcon: false,
        duration: 5000,
      });
    }
  };

  // ✅ 추가: 재전송 확인 핸들러
  const handleResendConfirm = () => {
    setShowResendModal(false);
    handleSend();
  };

  // Enter 키로 Send 버튼 실행
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSendClick();
    }
  };

  return (
    <>
      <InputWrapper>
        {props.label && <Label htmlFor={props.name}>{props.label}</Label>}
        <PasswordInputContainer>
          <StyledInput
            {...props}
            id={props.name}
            type="email"
            name="email"
            placeholder={props.placeholder || 'your@email.com'}
            autoComplete="off"
            autoCorrect="off"
            state={props.error ? 'error' : successMessage ? 'success' : 'default'}
            onKeyDown={handleKeyDown}
          />
          {onSend && (
            <ActionButton
              type="button"
              onClick={handleSendClick}
              disabled={isLoading || props.disabled}
              $isSent={internalIsSent}
            >
              {getButtonText()}
            </ActionButton>
          )}
        </PasswordInputContainer>
        {props.error && <ErrorText>{props.error}</ErrorText>}
        {!props.error && successMessage && <EmailSuccessText>{successMessage}</EmailSuccessText>}
      </InputWrapper>

      <Modal
        isOpen={showResendModal}
        onClose={() => setShowResendModal(false)}
        size="custom"
        maxWidth='344px'

        terms={false}
        autoHeight
      >
        <ResendModalContent>
          <ModalTitle>Resend Verification Code</ModalTitle>
          <ModalMessage>Do you want to resend the verification code?</ModalMessage>
          <ModalButtonContainer>
            <GradientButton onClick={() => setShowResendModal(false)} variant="gray">
              Cancel
            </GradientButton>
            <GradientButton onClick={handleResendConfirm}>Resend</GradientButton>
          </ModalButtonContainer>
        </ResendModalContent>
      </Modal>
    </>
  );
};

// 패스워드 Input 컴포넌트
export const PasswordInput: React.FC<PasswordInputProps> = ({
  onTogglePassword,
  helperText,
  autoComplete = 'off',
  ...props
}) => {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  const handleTogglePassword = () => {
    setIsPasswordVisible(!isPasswordVisible);
    onTogglePassword?.();
  };

  return (
    <InputWrapper>
      {props.label && <Label htmlFor={props.name}>{props.label}</Label>}
      <PasswordInputContainer>
        <StyledInput
          {...props}
          id={props.name}
          type={isPasswordVisible ? 'text' : 'password'}
          placeholder={props.placeholder || '••••••••'}
          autoComplete={autoComplete}
          state={props.error ? 'error' : 'default'}
        />
        <ToggleButton
          type="button"
          onClick={handleTogglePassword}
          aria-label={isPasswordVisible ? 'Hide password' : 'Show password'}
        >
          <Icon name={isPasswordVisible ? 'hidePassword' : 'showPassword'} size={20} />
        </ToggleButton>
      </PasswordInputContainer>
      {!props.error && helperText && <HelperText>{helperText}</HelperText>}
      {props.error && <ErrorText>{props.error}</ErrorText>}
    </InputWrapper>
  );
};

// 패스워드 확인 Input 컴포넌트
export const PasswordConfirmInput: React.FC<PasswordConfirmProps> = ({ value, onChange, onBlur, ...props }) => {
  return (
    <PasswordInput
      {...props}
      name="passwordConfirm"
      placeholder="Up to 18 characters. Required: letters, numbers, and symbols"
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      error={props.error}
    />
  );
};

// 이름 Input 컴포넌트
export const NameInput: React.FC<BaseInputProps> = props => {
  return <BaseInput {...props} type="text" name="name" placeholder={props.placeholder || 'Please enter your name'} />;
};

// 이메일 인증번호 Input 컴포넌트
export const EmailVerificationInput: React.FC<EmailVerificationInputProps> = ({
  onVerify,
  verifyButtonText = 'Verify',
  isLoading = false,
  isVerified = false,
  successMessage = '인증이 완료되었습니다.',
  ...props
}) => {
  // Enter 키로 Verify 버튼 실행
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && onVerify && !isLoading && !props.disabled && !isVerified) {
      e.preventDefault();
      onVerify();
    }
  };

  return (
    <InputWrapper>
      {props.label && <Label htmlFor={props.name}>{props.label}</Label>}
      <InputButtonContainer>
        <StyledInput
          {...props}
          id={props.name}
          type="text"
          name="verificationCode"
          placeholder={props.placeholder || 'Check your inbox and spam folder'}
          autoComplete="off"
          title="Verification Code"
          state={props.error ? 'error' : isVerified ? 'success' : 'default'}
          $hasButton={!!onVerify}
          $isVerified={isVerified}
          disabled={props.disabled || isVerified}
          onKeyDown={handleKeyDown}
        />
        {onVerify && (
          <ActionButton
            type="button"
            onClick={onVerify}
            disabled={isLoading || props.disabled || isVerified}
            $isVerified={isVerified}
          >
            {isLoading ? <LoadingSpinnerSmall /> : isVerified ? 'Verified' : verifyButtonText}
          </ActionButton>
        )}
      </InputButtonContainer>
      {props.error && !isVerified && <ErrorText>{props.error}</ErrorText>}
      {isVerified && !props.error && <SuccessText>{successMessage}</SuccessText>}
    </InputWrapper>
  );
};

// =============== Remport Input 컴포넌트 ===============

export const SourceInput: React.FC<BaseInputProps> = props => {
  return <BaseInput {...props} type="text" name="source" maxLength={30} />;
};

export const DateInput: React.FC<BaseInputProps> = ({
  name,
  placeholder = 'YYYY-MM-DD',
  value,
  onChange,
  onBlur,
  error,
  disabled,
  required,
  autoFocus,
  label,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const datePickerRef = useRef<DatePicker>(null);

  // Convert string value to Date object
  const selectedDate = value ? new Date(value) : null;

  const handleDateChange = (date: Date | null) => {
    if (date && onChange) {
      // Format date as YYYY-MM-DD
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const formattedDate = `${year}-${month}-${day}`;

      // Create a synthetic event
      const syntheticEvent = {
        target: {
          name,
          value: formattedDate,
        },
      } as React.ChangeEvent<HTMLInputElement>;

      onChange(syntheticEvent);
    }
    setIsOpen(false);
  };

  const handleIconClick = () => {
    setIsOpen(!isOpen);
  };

  return (
    <InputWrapper>
      {label && (
        <Label htmlFor={name}>
          {label}
          {required && <RequiredAsterisk>*</RequiredAsterisk>}
        </Label>
      )}
      <DateInputContainer>
        <DatePicker
          ref={datePickerRef}
          selected={selectedDate}
          onChange={handleDateChange}
          onBlur={onBlur}
          disabled={disabled}
          dateFormat="yyyy-MM-dd"
          placeholderText={placeholder}
          autoFocus={autoFocus}
          open={isOpen}
          onClickOutside={() => setIsOpen(false)}
          todayButton="Today"
          customInput={
            <StyledInput
              id={name}
              name={name}
              state={error ? 'error' : 'default'}
              readOnly
            />
          }
          popperPlacement="top"
        />
        <CalendarIconButton
          type="button"
          onClick={handleIconClick}
          disabled={disabled}
          aria-label="Open calendar"
        >
          <Icon name="calendar" size={20} />
        </CalendarIconButton>
      </DateInputContainer>
      {error && <ErrorText>{error}</ErrorText>}
    </InputWrapper>
  );
};

export const DescriptionInput: React.FC<DescriptionInputProps> = ({
  name,
  placeholder,
  value,
  onChange,
  onBlur,
  error,
  disabled,
  required,
  autoFocus,
  label,
  maxLength = 300,
  height = 200,
}) => {
  return (
    <InputWrapper>
      {label && (
        <Label htmlFor={name}>
          {label}
          {required && <RequiredAsterisk>*</RequiredAsterisk>}
        </Label>
      )}
      <StyledTextarea
        id={name}
        name={name}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        disabled={disabled}
        required={required}
        autoFocus={autoFocus}
        maxLength={maxLength}
        $height={height}
        $state={error ? 'error' : 'default'}
      />
      {error && <ErrorText>{error}</ErrorText>}
    </InputWrapper>
  );
};

interface FileInputProps extends Omit<BaseInputProps, 'onChange'> {
  maxFileSizeMB?: number;
  accept?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export const FileInput: React.FC<FileInputProps> = ({
  name,
  label,
  error,
  disabled,
  required,
  onChange,
  maxFileSizeMB = 10,
  accept = 'image/jpeg,image/png',
}) => {
  const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { showSnackbar } = useSnackbarStore();

  // accept 문자열에서 확장자 추출 (예: "image/jpeg,image/png" -> "JPG/PNG")
  const getAcceptedExtensions = () => {
    const mimeToExt: Record<string, string> = {
      'image/jpeg': 'JPG',
      'image/png': 'PNG',
      'image/gif': 'GIF',
      'image/webp': 'WEBP',
      'application/pdf': 'PDF',
    };
    return accept
      .split(',')
      .map(mime => mimeToExt[mime.trim()] || mime.trim())
      .join('/');
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const acceptedMimes = accept.split(',').map(mime => mime.trim());
    const maxSizeBytes = maxFileSizeMB * 1024 * 1024;

    // 파일 검증
    for (const file of Array.from(files)) {
      // 파일 형식 검증
      if (!acceptedMimes.includes(file.type)) {
        showSnackbar(`Only ${getAcceptedExtensions()} files can be uploaded.`, {
          showErrorIcon: true,
          showCloseIcon: true,
        });
        // input 초기화
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      // 파일 용량 검증
      if (file.size > maxSizeBytes) {
        showSnackbar(`Files larger than ${maxFileSizeMB} MB cannot be uploaded.`, {
          showErrorIcon: true,
          showCloseIcon: true,
        });
        // input 초기화
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }
    }

    // 모든 검증 통과
    setSelectedFiles(files);
    onChange?.(e);
  };

  const getFileNames = () => {
    if (!selectedFiles || selectedFiles.length === 0) {
      return `Attach a ${getAcceptedExtensions()} (max ${maxFileSizeMB}MB).`;
    }
    return Array.from(selectedFiles)
      .map(file => file.name)
      .join(', ');
  };

  return (
    <InputWrapper>
      {label && (
        <Label htmlFor={name}>
          {label}
          {required && <RequiredAsterisk>*</RequiredAsterisk>}
        </Label>
      )}
      <FileInputContainer>
        <FileInputPlaceholder $hasFiles={!!selectedFiles && selectedFiles.length > 0}>
          {getFileNames()}
        </FileInputPlaceholder>
        <HiddenFileInput
          ref={fileInputRef}
          id={name}
          type="file"
          name={name}
          onChange={handleFileChange}
          disabled={disabled}
          required={required}
          accept={accept}
          multiple
        />
        <GradientButton
          type="button"
          onClick={handleButtonClick}
          disabled={disabled}
          variant="primary"
          icon="search2"
          fontSize={16}
        >
          Browse Files
        </GradientButton>
      </FileInputContainer>
      {error && <ErrorText>{error}</ErrorText>}
    </InputWrapper>
  );
};


// 스타일 컴포넌트들
const InputWrapper = styled(FormField)`
  margin-top: 32px;
`;

const Label = styled(FormLabel)`
  top: -32px;
  left: 0px;
`;

const RequiredAsterisk = styled.span`
  color: ${({ theme }) => theme.colors.errorRed};
  margin-left: 4px;
`;

const DateInputContainer = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;

  /* Custom react-datepicker styles */
  .react-datepicker-wrapper {
    width: 100%;
  }

  .react-datepicker-popper {
    z-index: 9999;
  }

  .react-datepicker {
    font-family: ${({ theme }) => theme.fonts.pretendard};
    border: 1px solid ${({ theme }) => theme.colors.borderGray};
    border-radius: 10px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  .react-datepicker__header {
    background-color: ${({ theme }) => theme.colors.bgWhite};
    border-bottom: 1px solid ${({ theme }) => theme.colors.borderGray};
    border-radius: 10px 10px 0 0;
    padding-top: 12px;
  }

  .react-datepicker__current-month {
    font-size: 16px;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.fontBlackText};
  }

  .react-datepicker__day-name,
  .react-datepicker__day {
    width: 2.5rem;
    line-height: 2.5rem;
    margin: 0.2rem;
    color: ${({ theme }) => theme.colors.fontBlackText};
  }

  .react-datepicker__day:hover {
    background-color: ${({ theme }) => theme.colors.bgGray[100]};
    border-radius: 50%;
  }

  .react-datepicker__day--selected,
  .react-datepicker__day--keyboard-selected {
    background-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.fontWhite};
    border-radius: 50%;
    font-weight: 600;

    &:hover {
      background-color: ${({ theme }) => theme.colors.primary};
    }
  }

  .react-datepicker__day--disabled {
    color: ${({ theme }) => theme.colors.fontGray[200]};
    cursor: not-allowed;

    &:hover {
      background-color: transparent;
    }
  }

  .react-datepicker__navigation {
    top: 12px;
  }

  .react-datepicker__navigation-icon::before {
    border-color: ${({ theme }) => theme.colors.fontGray[400]};
  }

  .react-datepicker__navigation:hover *::before {
    border-color: ${({ theme }) => theme.colors.primary};
  }

  .react-datepicker__today-button {
    background: ${({ theme }) => theme.colors.bgGray[100]};
    border-top: 1px solid ${({ theme }) => theme.colors.borderGray};
    padding: 12px;
    text-align: center;
    cursor: pointer;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.primary};
    transition: all 0.2s ease;

    &:hover {
      background: ${({ theme }) => theme.colors.primary};
      color: ${({ theme }) => theme.colors.fontWhite};
    }
  }
`;

const CalendarIconButton = styled.button`
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.fontGray[400]};
  transition: color 0.2s ease;

  &:hover:not(:disabled) {
    color: ${({ theme }) => theme.colors.primary};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

const FileInputContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
`;

const FileInputPlaceholder = styled.div<{ $hasFiles: boolean }>`
  flex: 1;
  padding: 12px 16px;
  border: 1px solid ${({ theme }) => theme.colors.borderGray};
  border-radius: 10px;
  background: ${({ theme }) => theme.colors.bgGray[100]};
  color: ${({ theme, $hasFiles }) => ($hasFiles ? theme.colors.fontBlackText : theme.colors.fontGray[300])};
  font-size: 16px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-height: 48px;
  display: flex;
  align-items: center;
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const StyledInput = styled(FormInput)<{
  $hasButton?: boolean;
  $isVerified?: boolean;
}>`
  padding-right: ${({ $hasButton }) => ($hasButton ? '90px' : '16px')};
  background: ${({ theme, $isVerified }) => ($isVerified ? theme.colors.bgWhite : theme.colors.bgGray[100])};

  &:focus {
    background: ${({ theme }) => theme.colors.bgWhite};
  }

  &:disabled {
    background: ${({ theme, $isVerified }) => ($isVerified ? theme.colors.bgWhite : theme.colors.bgGray[200])};
    cursor: ${({ $isVerified }) => ($isVerified ? 'default' : 'not-allowed')};
    opacity: ${({ $isVerified }) => ($isVerified ? 1 : 0.6)};
  }
`;

const StyledTextarea = styled.textarea<{
  $height?: number;
  $state?: 'default' | 'error';
}>`
  width: 100%;
  height: ${({ $height }) => $height}px;
  padding: 16px;
  font-size: 16px;
  font-family: inherit;
  line-height: 1.5;
  border: 1px solid ${({ theme, $state }) => ($state === 'error' ? theme.colors.errorRed : theme.colors.borderGray)};
  border-radius: 10px;
  background: ${({ theme }) => theme.colors.bgGray[100]};
  color: ${({ theme }) => theme.colors.fontBlackText};
  resize: vertical;
  transition: all 0.2s ease;

  &::placeholder {
    color: ${({ theme }) => theme.colors.fontGray[300]};
  }

  &:focus {
    outline: none;
    background: ${({ theme }) => theme.colors.bgWhite};
    border-color: ${({ theme, $state }) => ($state === 'error' ? theme.colors.errorRed : theme.colors.primary)};
  }

  &:disabled {
    background: ${({ theme }) => theme.colors.bgGray[200]};
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const EmailSuccessText = styled(FormSuccess)``;

const PasswordInputContainer = styled(SharedPasswordInputContainer)``;

const ToggleButton = styled(ToggleIconButton)`
  font-size: 14px;
`;

const ActionButton = styled(InputActionButton)<{ $isSent?: boolean; $isVerified?: boolean }>`
  background: ${({ theme, $isSent, $isVerified }) =>
    $isVerified
      ? theme.colors.bgWhite || theme.colors.successGreen
      : $isSent
        ? theme.colors.bgGray || theme.colors.bgWhite
        : theme.colors.bgWhite};

  &:hover:not(:disabled) {
    background: ${({ theme, $isSent, $isVerified }) =>
      $isVerified
        ? theme.colors.bgWhite || theme.colors.successGreen
        : $isSent
          ? theme.colors.bgGray || theme.colors.bgWhite
          : theme.colors.btnBgGray || theme.colors.bgWhite};
    opacity: 0.9;
  }

  &:disabled {
    background: ${({ theme, $isVerified }) =>
      $isVerified ? theme.colors.bgWhite || theme.colors.successGreen : theme.colors.btnDisabled};
    cursor: ${({ $isVerified }) => ($isVerified ? 'default' : 'not-allowed')};
    opacity: ${({ $isVerified }) => ($isVerified ? 1 : 0.7)};
  }
`;

const SuccessText = styled(FormSuccess)``;

const InputButtonContainer = styled(SharedPasswordInputContainer)``;

const ResendModalContent = styled.div`
  text-align: left;
`;

const ModalTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  line-height: 22px;
  color: ${({ theme }) => theme.colors.fontBlackText};
  margin-bottom: 12px;
`;

const ModalMessage = styled.p`
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: ${({ theme }) => theme.colors.fontBlackText};
  margin-bottom: 24px;
`;

const ModalButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;

  > button {
   font-size: 14px;
   height:36px;
   width: 143px;
  }
`;

const HelperText = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.fontGray[400]};
  margin-top: 8px;
  margin-bottom: 0;
  line-height: 1.4;
`;
